import moment, { Moment } from 'moment';
import React, { useContext, useLayoutEffect, useMemo, useRef } from 'react';
import { Box } from '../../../Box';
import { DateRangePickerContext } from '../../context';
import { Month, MonthProps } from '../../Month';
import { monthsInRange } from '../util';
import { useVisibilityMap } from './hooks';
import css from './index.module.scss';
import { MonthRefs } from './types';
import { getMonthHeight } from './util';

interface MonthScrollerProps
  extends Omit<MonthProps, 'month' | 'minDate' | 'maxDate'>,
    Required<Pick<MonthProps, 'minDate' | 'maxDate'>> {
  viewport: HTMLDivElement | null;
}

const getMonthKey = (d: Moment) => d.format('MM/YYYY');

export const MonthScroller: React.FC<MonthScrollerProps> = ({
  minDate,
  maxDate,
  ranges,
  focusedRange,
  viewport,
  ...rest
}) => {
  const { v2 } = useContext(DateRangePickerContext);
  // scroll to focused range start on mount, falling back to current month
  const scrollMonth = useRef(ranges[focusedRange].start ?? moment());
  const months = useMemo(() => monthsInRange(minDate, maxDate), [minDate, maxDate]);
  const monthRefs = useMemo(
    () =>
      months.reduce<MonthRefs>((acc, month) => {
        acc[getMonthKey(month)] = React.createRef();
        return acc;
      }, {}),
    [months],
  );
  const visibilityMap = useVisibilityMap(viewport, monthRefs);

  useLayoutEffect(() => {
    monthRefs[getMonthKey(scrollMonth.current)]?.current?.scrollIntoView({
      behavior: 'auto',
      block: 'center',
    });
    // Even though there is no scrollbar on the parent Calendar div, the browser will
    // still "scroll" it up when the current month is scrolled into view programatically.
    // This causes the day names at the top to be cut off, so we just go ahead and reset
    // the scroll position to 0, 0.
    viewport?.parentElement?.parentElement?.scrollTo(0, 0);
  }, [monthRefs, viewport]);

  return (
    <Box data-component="MonthScroller" className={css.months} padding={16}>
      {months.map(month => {
        const key = getMonthKey(month);
        const monthRef = monthRefs[key];
        const isVisible = monthRef?.current && visibilityMap.get(monthRef.current);
        const dimensions = !isVisible && {
          width: v2 ? 400 : 308,
          height: getMonthHeight(month),
        };
        return (
          <Box key={key} ref={monthRef} {...dimensions}>
            {isVisible && (
              <Month
                month={month}
                ranges={ranges}
                focusedRange={focusedRange}
                minDate={minDate}
                maxDate={maxDate}
                {...rest}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};
